import { Button, Classes, FormGroup, InputGroup } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { getIntentClassName } from 'vet-bones/utils';

import { useAppDispatch } from 'src/ui/app/hooks';
import { Auth0Link } from 'src/ui/components/Auth0Link';
import { AzureLink } from 'src/ui/components/AzureLink';
import { Dialog } from 'src/ui/components/Dialog';
import { DuoLink } from 'src/ui/components/DuoLink';
import { GoogleLink } from 'src/ui/components/GoogleLink';
import { Loading } from 'src/ui/components/Loading';
import { OktaLink } from 'src/ui/components/OktaLink';
import { OpenIDLink } from 'src/ui/components/OpenIDLink';
import { PingLink } from 'src/ui/components/PingLink';
import {
  LAUNCHPAD_LOGIN_KERBEROS_AUTH_SUBMIT_BUTTON,
  LAUNCHPAD_LOGIN_SHARED_USER_AUTH_SUBMIT_BUTTON,
} from 'src/ui/constants/testIds';
import { queueNotification } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useProfileQuery } from 'src/ui/graph/types';
import { components } from 'src/ui/templates/copy';
import {
  isAuth0AuthEnabled,
  isAzureAuthEnabled,
  isDuoAuthEnabled,
  isGoogleAuthEnabled,
  isKerberosAuthEnabled,
  isOktaAuthEnabled,
  isOpenIDAuthEnabled,
  isPingAuthEnabled,
  isSharedUserAuthEnabled,
} from 'src/ui/utils/features';
import { redirectToUrl } from 'src/ui/utils/window';

export const Login: React.VFC = () => {
  const dispatch = useAppDispatch();
  const [isDialogOpen, setIsDialogOpen] = React.useState(true);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const {
    data: profileData,
    isLoading: profileDataIsLoading,
  } = useProfileQuery(graphQLClient);

  React.useEffect(() => {
    if (profileDataIsLoading || !profileData || !profileData.profile) {
      return;
    }
    if (profileData.profile.is_authenticated) {
      setIsDialogOpen(false);
      redirectToUrl('/');
    }
  }, [profileData, profileDataIsLoading]);

  const auth0AuthEnabled = isAuth0AuthEnabled();
  const azureAuthEnabled = isAzureAuthEnabled();
  const googleAuthEnabled = isGoogleAuthEnabled();
  const openidAuthEnabled = isOpenIDAuthEnabled();
  const pingAuthEnabled = isPingAuthEnabled();
  const oktaAuthEnabled = isOktaAuthEnabled();
  const duoAuthEnabled = isDuoAuthEnabled();
  const sharedUserAuthEnabled = isSharedUserAuthEnabled();
  const kerberosAuthEnabled = isKerberosAuthEnabled();

  const anySSOEnabled =
    auth0AuthEnabled ||
    azureAuthEnabled ||
    googleAuthEnabled ||
    openidAuthEnabled ||
    pingAuthEnabled ||
    oktaAuthEnabled;

  const auth0Button = auth0AuthEnabled ? <Auth0Link /> : null;
  const azureButton = azureAuthEnabled ? <AzureLink /> : null;
  const googleButton = googleAuthEnabled ? <GoogleLink /> : null;
  const openidButton = openidAuthEnabled ? <OpenIDLink /> : null;
  const pingButton = pingAuthEnabled ? <PingLink /> : null;
  const oktaButton = oktaAuthEnabled ? <OktaLink /> : null;
  const duoButton = duoAuthEnabled ? <DuoLink /> : null;

  const handleSharedUserLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoggingIn(true);

    try {
      const res = await fetch('/auth/shared-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
        redirect: 'follow',
      });

      if (res.redirected) {
        // user should be redirected in the success case
        window.location.replace(res.url);
        setIsDialogOpen(false);
        return;
      }

      let errorMsg = 'Invalid username or password';
      if (res.status >= 500) {
        errorMsg = 'An unexpected server error occurred. Please try again.';
      }

      try {
        const data = await res.json();
        if (data?.error) {
          errorMsg = data.error;
        }
      } catch (err) {
        // server should always return JSON, but if it doesn't, log the error
        console.error('Failed to parse error response');
      }

      dispatch(queueNotification(errorMsg));
    } catch (error) {
      dispatch(
        queueNotification(
          'An unexpected error occurred. Please try again or check with your system administrator if the problem persists.'
        )
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleKerberosLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoggingIn(true);

    try {
      const res = await fetch('/auth/kerberos-login', {
        method: 'POST',
        redirect: 'follow',
        credentials: 'include',
      });

      if (res.redirected) {
        // user should be redirected in the success case
        window.location.replace(res.url);
        setIsDialogOpen(false);
        return;
      }

      let errorMsg =
        'Invalid kerberos credentials. Please verify your Kerberos credentials and try again.';
      if (res.status >= 500) {
        errorMsg = 'An unexpected server error occurred. Please try again.';
      }

      try {
        const data = await res.json();
        if (data?.error) {
          errorMsg = data.error;
        }
      } catch (err) {
        // server should always return JSON, but if it doesn't, log the error
        console.error('Failed to parse error response');
      }

      dispatch(queueNotification(errorMsg));
    } catch (error) {
      dispatch(
        queueNotification(
          'An unexpected error occurred. Please try again or check with your system administrator if the problem persists.'
        )
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  return isDialogOpen ? (
    <Dialog
      icon={IconNames.OFFLINE}
      title={components.launchpad.login.dialogTitle}
      className={classNames('sd-dialog-launchpad-login')}
      isCloseButtonShown={false}
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
      isOpen={isDialogOpen}
    >
      <div className={`${Classes.DIALOG_BODY}`}>
        {sharedUserAuthEnabled ? (
          <>
            <form onSubmit={handleSharedUserLogin}>
              <FormGroup label="Username" labelFor="username-input">
                <InputGroup
                  id="username-input"
                  placeholder="Enter username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  large
                />
              </FormGroup>

              <FormGroup label="Password" labelFor="password-input">
                <InputGroup
                  id="password-input"
                  placeholder="Enter password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  large
                />
              </FormGroup>

              <Button
                data-testid={LAUNCHPAD_LOGIN_SHARED_USER_AUTH_SUBMIT_BUTTON}
                className={classNames(
                  'sd-dialog-launchpad-login__submit-button',
                  getIntentClassName('sd-green')
                )}
                type="submit"
                text="Login"
                loading={isLoggingIn}
                large
                fill
              />
            </form>
          </>
        ) : null}
        {sharedUserAuthEnabled && kerberosAuthEnabled ? (
          <div className="sd-dialog-launchpad-login__divider">
            <strong>or</strong>
          </div>
        ) : null}

        {kerberosAuthEnabled ? (
          <form onSubmit={handleKerberosLogin}>
            <Button
              data-testid={LAUNCHPAD_LOGIN_KERBEROS_AUTH_SUBMIT_BUTTON}
              className={classNames(
                'sd-dialog-launchpad-login__submit-button',
                getIntentClassName('sd-green')
              )}
              type="submit"
              text="Login with Kerberos"
              loading={isLoggingIn}
              large
              fill
            />
          </form>
        ) : null}

        {(sharedUserAuthEnabled || kerberosAuthEnabled) && anySSOEnabled ? (
          <div className="sd-dialog-launchpad-login__divider">
            <strong>or</strong>
          </div>
        ) : null}

        {anySSOEnabled ? (
          <div className="controls">
            {auth0Button}
            {googleButton}
            {azureButton}
            {openidButton}
            {pingButton}
            {oktaButton}
            {duoButton}
          </div>
        ) : null}
      </div>
    </Dialog>
  ) : (
    <Loading />
  );
};
